import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GraduationCap, Loader2, ArrowRight } from 'lucide-react';
import { usePortalAuth, formatApiError } from '../../contexts/PortalAuthContext';

export default function PortalLogin() {
  const { login, user } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Redirect after render (not during) to avoid React warning
  useEffect(() => {
    if (user) navigate('/portal', { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
      navigate('/portal');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Backdrop pattern */}
      <div className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(245,158,11,0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(244,63,94,0.10) 0%, transparent 40%)' }}
      />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center group">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center group-hover:scale-105 transition">
            <GraduationCap className="w-7 h-7 text-slate-950" />
          </div>
          <div className="text-left">
            <div className="text-lg font-semibold tracking-wide text-white">AUDIO &amp; CO</div>
            <div className="text-xs text-slate-400">Training Members Portal</div>
          </div>
        </Link>

        <div className="bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-2xl font-semibold text-white mb-1">Welcome back</h1>
          <p className="text-slate-400 text-sm mb-6">Sign in to access your modules, journal, and cohort.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="portal-login-form">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                data-testid="portal-login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-slate-950/80 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none"
                data-testid="portal-login-password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="portal-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-slate-950 font-semibold rounded-lg px-4 py-2.5 transition flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="portal-login-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-xs text-slate-500 mt-6 text-center">
            Don&apos;t have an account yet?{' '}
            <Link to="/training" className="text-amber-400 hover:underline">Enrol on the programme →</Link>
          </p>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6">
          Access codes are sent by email after your Wise payment is confirmed.
        </p>
      </div>
    </div>
  );
}
